import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { fleetKeys } from '@/hooks/fleet-keys';
import { ApiError, apiClient, fleetPath } from '@/lib/api-client';

/** Mutable fields of a server — mirrors the backend UpdateServerDto. */
export interface UpdateServerBody {
  status?: string;
  /** Rollout gate: 'staging' (internal-only) | 'production' (all users). */
  environment?: string;
  notes?: string;
  provider_account?: string;
  region_continent?: string;
  region_country?: string;
  region_city?: string;
  vm_type?: string;
  /** Public DNS hostname (e.g. proxy-us.getpocketvpn.com). Surfaced by
   * POST /proxy/session to the browser extension; without this, the
   * extension TLS-validates against publicIp which mismatches the cert. */
  dns_hostname?: string;
  /** Full protocol set — replaces the server's current protocols. */
  protocols?: string[];
  /** Full capability set — replaces the server's current capabilities. */
  capabilities?: string[];
}

interface UpdateServerVariables {
  name: string;
  patch: UpdateServerBody;
}

/**
 * PATCH /api/admin/fleet/servers/:name — partial update of a server.
 *
 * On success the server-detail and summary queries are invalidated so the
 * adapted detail view refetches. (The PATCH response is the raw camelCase
 * server; invalidating lets `use-fleet-server` re-fetch and re-adapt it
 * rather than caching an un-adapted shape.)
 */
export function useUpdateServer(): UseMutationResult<unknown, ApiError, UpdateServerVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, patch }) =>
      apiClient.patch<unknown>(fleetPath(`/servers/${encodeURIComponent(name)}`), patch),
    onSuccess: (_data, { name }) => {
      void queryClient.invalidateQueries({ queryKey: fleetKeys.server(name) });
      void queryClient.invalidateQueries({ queryKey: fleetKeys.summary() });
    },
  });
}
